import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as moment from 'moment-timezone';
import { catchError, map, Observable, throwError } from 'rxjs';
import {
  AssignDriverRequest,
  AssignDriverResponse,
  DriverDetails,
  DriverDetailsResponse,
} from 'src/app/models/driver/driver.model';
import {
  ActiveDriver,
  DriverFilterType,
  DriverForService,
  GetMapDriversRequest,
  MapDriversResponse,
  ServiceFilterData,
  ServicesInZoneResponse,
} from 'src/app/models/map-drivers/map-drivers.model';
import { ZoneServiceManagerDataResponse } from 'src/app/models/pool-manager/service-manager.model';
import { OrderData } from 'src/app/models/service/service.model';
import { environment } from 'src/environments/environment';


export interface ServiceFilterStartEndData {
  StartDate: string | null | undefined;
  EndDate: string | null | undefined;
  ZoneGuid: string | null | undefined;
}

@Injectable({
  providedIn: 'root',
})
export class MapaServiceService {
  url = environment.url;

  constructor(private http: HttpClient) {}

  getZones(): Observable<ZoneServiceManagerDataResponse> {
    return this.http.get<ZoneServiceManagerDataResponse>(`${this.url}/zones`);
  }

  getDrivers() {
    return this.http.get(`${this.url}/drivers?nameFilterQuery=cesar`);
  }

  getServices(filter: ServiceFilterData): Observable<ServicesInZoneResponse> {
    return this.http.post<ServicesInZoneResponse>(
      `${this.url}/services-map/services-in-zone`,
      filter
    );
  }

  /**
   * Utilidad opcional: aplana todos los `activeDrivers`,
   * elimina duplicados y filtra los inactivos si se desea.
   */
  getFlatActiveDrivers(
    filter: ServiceFilterData,
    { onlyActives = false } = {}
  ): Observable<ActiveDriver[]> {
    return this.getServices(filter).pipe(
      map((blocks) =>
        blocks
          .flatMap((b) => b.activeDrivers)
          .reduce<Record<string, ActiveDriver>>((acc, driver) => {
            acc[driver.driverId] = driver; // dedupe por driverId
            return acc;
          }, {})
      ),
      map((dict) =>
        Object.values(dict).filter((d) =>
          onlyActives ? d.general_info.isActive : true
        )
      )
    );
  }

  getDriver(driverId: string): Observable<DriverDetailsResponse> {
    return this.http.get<DriverDetailsResponse>(
      `${this.url}/drivers?driverIdFilter=${driverId}`
    );
  }

  getMapDrivers(request: GetMapDriversRequest): Observable<MapDriversResponse> {
    return this.http.post<MapDriversResponse>(
      `${this.url}/services-map/get-drivers`,
      request
    );
  }

  getFlatDrivers(
    orderHash: string,
    filterType: DriverFilterType = DriverFilterType.Proximity
  ): Observable<DriverForService[]> {
    return this.getMapDrivers({ orderHash, filterType }).pipe(
      map((blocks) =>
        blocks
          .flatMap((b) => b.driversForService)
          .reduce<Record<string, DriverForService>>((acc, driver) => {
            // deduplicamos por driverId
            acc[driver.driverId] = driver;
            return acc;
          }, {})
      ),
      map((dict) =>
        Object.values(dict).sort(
          (a, b) => a.distanceToService - b.distanceToService
        )
      )
    );
  }

  /**
   * Asigna un conductor (o coche escoba) a un nodo.
   * @returns `Observable` que emite la respuesta tipada.
   */
  assignDriver(payload: AssignDriverRequest): Observable<AssignDriverResponse> {
    return this.http
      .post<AssignDriverResponse>(`${this.url}/services/assign-driver`, payload)
      .pipe(
        // Normalizamos errores HTTP a un objeto del mismo tipo,
        // para que el componente no tenga que mezclar `error` y `next`.
        map((res) => res ?? { wasSuccessful: false, statusCode: 'UNKNOWN' }),
        catchError((err) =>
          throwError(
            () =>
              ({
                wasSuccessful: false,
                statusCode: err.status ?? 'HTTP_ERROR',
              } as AssignDriverResponse)
          )
        )
      );
  }

  getAssignedServices(
    data: ServiceFilterStartEndData
  ): Observable<OrderData[]> {
    return this.http.get<OrderData[]>(
      `${this.url}/routeMapSystem/GetAssignedPoolServices?startDate=${moment(
        data.StartDate
      ).format('MM/DD/yyyy')}&endDate=${moment(data.EndDate).format(
        'MM/DD/yyyy'
      )}&zoneGuid=${data.ZoneGuid}`
    );
  }

  getUnassignedServices(
    data: ServiceFilterStartEndData
  ): Observable<OrderData[]> {
    return this.http.get<OrderData[]>(
      `${this.url}/routeMapSystem/GetNotAssignedPoolServices?startDate=${moment(
        data.StartDate
      ).format('MM/DD/yyyy')}&endDate=${moment(data.EndDate).format(
        'MM/DD/yyyy'
      )}&zoneGuid=${data.ZoneGuid}`
    );
  }
}
